/**
 * 图片工具函数
 * 用于前端图片处理，包括 WebP 转换
 */

/**
 * 判断文件是否为图片
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/')
}

/**
 * 判断文件是否已经是 WebP 格式
 */
export function isWebPFile(file: File): boolean {
  return file.type === 'image/webp'
}

/**
 * 判断文件是否需要转换为 WebP
 * - 图片文件需要转换（除了已经是 WebP 的）
 * - 非图片文件（如 PDF）不需要转换
 */
export function shouldConvertToWebP(file: File): boolean {
  return isImageFile(file) && !isWebPFile(file)
}

/**
 * 将图片文件转换为 WebP 格式
 * 
 * @param file - 原始图片文件
 * @param quality - 压缩质量 (0-1)，默认 0.85
 * @returns 转换后的 WebP Blob
 */
export async function convertToWebP(
  file: File,
  quality: number = 0.85
): Promise<Blob> {
  // 非图片文件或已经是 WebP，直接返回
  if (!shouldConvertToWebP(file)) {
    return file
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('无法创建 Canvas 上下文'))
      return
    }

    img.onload = () => {
      // 设置 canvas 尺寸与原图一致
      canvas.width = img.width
      canvas.height = img.height

      // 绘制图片到 canvas
      ctx.drawImage(img, 0, 0)

      // 转换为 WebP
      canvas.toBlob(
        (blob) => {
          // 释放 URL 对象
          URL.revokeObjectURL(img.src)

          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('WebP 转换失败'))
          }
        },
        'image/webp',
        quality
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('图片加载失败'))
    }

    // 创建临时 URL 加载图片
    img.src = URL.createObjectURL(file)
  })
}

/**
 * 处理文件上传前的转换
 * - 图片文件转换为 WebP
 * - 非图片文件保持原样
 * 
 * @param file - 原始文件
 * @param quality - WebP 压缩质量
 * @returns 处理后的文件（可能是 WebP Blob 或原始文件）
 */
export async function processFileForUpload(
  file: File,
  quality: number = 0.85
): Promise<{ blob: Blob; fileName: string; mimeType: string }> {
  if (shouldConvertToWebP(file)) {
    const webpBlob = await convertToWebP(file, quality)
    // 替换文件扩展名为 .webp
    const originalName = file.name
    const baseName = originalName.replace(/\.[^.]+$/, '')
    const newFileName = `${baseName}.webp`

    return {
      blob: webpBlob,
      fileName: newFileName,
      mimeType: 'image/webp',
    }
  }

  // 非图片文件保持原样
  return {
    blob: file,
    fileName: file.name,
    mimeType: file.type,
  }
}

/**
 * 获取图片尺寸
 */
export async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!isImageFile(file)) {
      reject(new Error('不是图片文件'))
      return
    }

    const img = new Image()

    img.onload = () => {
      URL.revokeObjectURL(img.src)
      resolve({ width: img.width, height: img.height })
    }

    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('图片加载失败'))
    }

    img.src = URL.createObjectURL(file)
  })
}

/**
 * 格式化文件大小
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {return '0 B'}

  const units = ['B', 'KB', 'MB', 'GB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${units[i]}`
}

/**
 * 验证文件类型
 */
export function validateFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type)
}

/**
 * 验证文件大小
 */
export function validateFileSize(file: File, maxSize: number): boolean {
  return file.size <= maxSize
}
